// pages/ShopPage.jsx
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useShopFilters } from '../hooks/useShopFilters';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import ShopHeroSlider from '../components/shop/ShopHeroSlider';
import FilterBarBit from '../components/shop/FilterBarBit';
import ProductGridBit from '../components/shop/ProductGridBit';
import NewsletterBit from '../components/bits/NewsletterBit';
import LuxuryLoading from '../components/ui/LuxuryLoading';
import '../styles/globals.css';
import {ProductService} from "../api/product.api";

const ShopPage = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState([]);
    const [concerns, setConcerns] = useState([]);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        fetchInitialData();

        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const fetchInitialData = async () => {
        try {
            setLoading(true);

            // Fetch products
            const productsData = await ProductService.getProducts({
                limit: 50,
                sort: 'popular'
            });
            console.log(productsData);
            setProducts(productsData || []);

            // Fetch categories
            const categoriesData = await ProductService.getCategories();
            setCategories(categoriesData || []);

            // For concerns, you might need a separate API endpoint
            // For now, extract from products or use a hardcoded list
            const allConcerns = productsData.reduce((acc, product) => {
                if (product.concerns) {
                    product.concerns.forEach(concern => {
                        if (!acc.includes(concern)) {
                            acc.push(concern);
                        }
                    });
                }
                return acc;
            }, []);
            setConcerns(allConcerns.map(concern => ({
                id: concern.toLowerCase().replace(/\s+/g, '-'),
                label: concern
            })));

        } catch (error) {
            console.error('Error fetching shop data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (searchQuery) => {
        try {
            const results = await ProductService.searchProducts(searchQuery);
            setProducts(results || []);
        } catch (error) {
            console.error('Error searching products:', error);
        }
    };

    const {
        filters,
        filteredProducts,
        isFiltering,
        activeFilterCount,
        sortOptions,
        priceRanges,
        updateFilter,
        resetFilters,
        clearFilter,
    } = useShopFilters(products);

    const handleFilterChange = (filterName, value) => {
        updateFilter(filterName, value);

        // If search query changed, trigger API search
        if (filterName === 'searchQuery' && value.trim()) {
            handleSearch(value);
        }
    };

    const handleClearFilter = (filterName) => {
        filterName ? clearFilter(filterName) : resetFilters();

        // Reset to initial products if clearing all filters
        if (!filterName) {
            fetchInitialData();
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-pearl">
                <Navbar />
                <div className="min-h-[60vh] flex items-center justify-center">
                    <LuxuryLoading size="large" />
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-pearl">
            {/* Fixed Navbar */}
            <div
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                    isScrolled
                        ? 'bg-white/100 backdrop-blur-sm shadow-soft'
                        : 'bg-transparent'
                }`}
            >
                <Navbar />
            </div>

            {/* Hero */}
            <section id="hero" className="relative w-full">
                <ShopHeroSlider />
            </section>

            {/* Filters */}
            <section
                id="filters"
                className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-nude/50"
            >
                <div className="container-luxury">
                    <FilterBarBit
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onClearFilter={handleClearFilter}
                        activeFilterCount={activeFilterCount}
                        categories={categories}
                        concerns={concerns}
                        sortOptions={sortOptions}
                        priceRanges={priceRanges}
                    />
                </div>
            </section>

            {/* Products */}
            <section id="products" className="section-spacing">
                <div className="container-luxury">
                    <ProductGridBit
                        filteredProducts={filteredProducts}
                        isFiltering={isFiltering}
                        products={products}
                    />
                </div>
            </section>

            {/* Newsletter */}
            <section id="newsletter" className="section-spacing bg-nude/30">
                <div className="container-luxury">
                    <NewsletterBit />
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ShopPage;